import React, { useEffect, useState } from 'react'
import { Box, Stack } from '@mui/material'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ThreeSixtyRoundedIcon from '@mui/icons-material/ThreeSixtyRounded'
import { useTranslation } from 'react-i18next'

import { BLUE_600, GRAY_300 } from 'constants/styling/theme'
import type { ProductGroupGalleryImage } from 'models/purchaseFlowRevamp'
import { MUIIconButton } from 'components/common/MUIIconButton'

interface Props {
  images: ProductGroupGalleryImage[]
  /** Resets the shown frame when the panel switches product. */
  resetKey?: string
}

/** The Vimeo player for a vimeo.com link, muted and without Vimeo's tracking; undefined for any other link. */
export const vimeoPlayerUrl = (videoUrl: string): string | undefined => {
  const id = videoUrl.match(/^https:\/\/vimeo\.com\/(\d+)$/)?.[1]
  return id ? `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&dnt=1&title=0&byline=0&portrait=0` : undefined
}

/** The Matterport player for a my.matterport.com tour link, started on open; undefined for any other link. */
export const matterportPlayerUrl = (tourUrl: string): string | undefined => {
  const model = tourUrl.match(/^https:\/\/my\.matterport\.com\/show\/\?m=([A-Za-z0-9]+)$/)?.[1]
  return model ? `https://my.matterport.com/show/?m=${model}&play=1&qs=1` : undefined
}

/** What a frame opens into: its video or its tour; undefined for a plain image. */
const playerUrlOf = (image: ProductGroupGalleryImage): string | undefined =>
  image.videoUrl ? vimeoPlayerUrl(image.videoUrl) : image.tourUrl ? matterportPlayerUrl(image.tourUrl) : undefined

const FRAME_SX = { display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', border: 0 } as const

/**
 * Example imagery for a product group: one frame at a time, with dots. A frame that is a video's or a tour's poster
 * gets a play button, and the player loads only once it is clicked, so opening the panel downloads no video or tour.
 */
export const ProductInfoGallery: React.FC<Props> = ({ images, resetKey }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const [at, setAt] = useState(0)
  const [isPlaying, setPlaying] = useState(false)

  useEffect(() => {
    setAt(0)
    setPlaying(false)
  }, [resetKey])

  if (images.length === 0) return null

  const shown = images[Math.min(at, images.length - 1)]
  const playerUrl = playerUrlOf(shown)
  // A tour frame carries no alt from the site; name it by what it opens, so the frame is never untitled.
  const frameLabel = shown.alt || (shown.tourUrl ? t('purchase_flow_revamp:product_info.view_tour') : shown.videoUrl ? t('purchase_flow_revamp:product_info.play_video') : '')
  const step = (by: number) => {
    setPlaying(false)
    setAt((current) => (current + by + images.length) % images.length)
  }

  return (
    <Stack gap={0.8}>

      <Box sx={{ position: 'relative', borderRadius: '0.8rem', overflow: 'hidden', backgroundColor: GRAY_300 }}>

        {playerUrl && isPlaying
          ? <Box
            component="iframe"
            src={playerUrl}
            title={frameLabel}
            allow="autoplay; fullscreen; picture-in-picture; xr-spatial-tracking"
            allowFullScreen
            sx={FRAME_SX}
          />
          : <Box component="img" src={shown.url} alt={frameLabel} loading="lazy" sx={FRAME_SX} />
        }

        {playerUrl && !isPlaying &&
          <MUIIconButton
            variant="filled"
            icon={shown.tourUrl ? <ThreeSixtyRoundedIcon /> : <PlayArrowRoundedIcon />}
            ariaLabel={t(shown.tourUrl ? 'purchase_flow_revamp:product_info.view_tour' : 'purchase_flow_revamp:product_info.play_video')}
            onClick={() => setPlaying(true)}
            sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          />
        }

        {images.length > 1 &&
          <>
            <MUIIconButton
              variant="filled"
              icon={<ChevronLeftRoundedIcon />}
              ariaLabel={t('purchase_flow_revamp:product_info.previous_image')}
              onClick={() => step(-1)}
              sx={{ position: 'absolute', top: '50%', left: '0.8rem', transform: 'translateY(-50%)' }}
            />

            <MUIIconButton
              variant="filled"
              icon={<ChevronRightRoundedIcon />}
              ariaLabel={t('purchase_flow_revamp:product_info.next_image')}
              onClick={() => step(1)}
              sx={{ position: 'absolute', top: '50%', right: '0.8rem', transform: 'translateY(-50%)' }}
            />
          </>
        }

      </Box>

      {images.length > 1 &&
        <Stack direction="row" gap={0.6} justifyContent="center" aria-hidden>
          {images.map((image, index) => (
            <Box
              key={image.url}
              sx={{
                width: '0.6rem',
                height: '0.6rem',
                borderRadius: '50%',
                backgroundColor: index === at ? BLUE_600 : GRAY_300,
              }}
            />
          ))}
        </Stack>
      }

    </Stack>
  )
}
